import React, { useState, useMemo } from 'react';
import { Typography, Button, Space, Input, Tooltip, Switch } from 'antd';
import { 
  CopyOutlined, 
  ExpandOutlined, 
  CompressOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { copyToClipboard, filterParamsForDisplay } from '../utils';

const ParamsViewer = ({ 
  inputParams, 
  title = 'Input Parameters',
  showReuseButton = true,
  onReuseParams,
  data 
}) => {
  const [expanded, setExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUrls, setShowUrls] = useState(false);

  const displayParams = useMemo(() => {
    return filterParamsForDisplay(inputParams);
  }, [inputParams]);

  const filteredParams = useMemo(() => {
    if (!searchTerm) return displayParams;
    
    const search = searchTerm.toLowerCase();
    const filtered = {};
    
    Object.entries(displayParams).forEach(([key, value]) => {
      const keyMatch = key.toLowerCase().includes(search);
      const valueMatch = String(value).toLowerCase().includes(search);
      if (keyMatch || valueMatch) {
        filtered[key] = value;
      }
    });
    
    return filtered;
  }, [displayParams, searchTerm]);

  const handleCopyAll = () => {
    copyToClipboard(JSON.stringify(displayParams, null, 2));
  };

  const renderValue = (value, key, depth = 0) => {
    if (value === null) return <span className="jdv-json-null">null</span>;
    if (value === undefined) return <span className="jdv-json-null">undefined</span>;
    if (typeof value === 'boolean') {
      return <span className="jdv-json-boolean">{value.toString()}</span>;
    }
    if (typeof value === 'number') {
      return <span className="jdv-json-number">{value}</span>;
    }
    if (typeof value === 'string') {
      // Check if it's a URL
      if (value.startsWith('http://') || value.startsWith('https://')) {
        // Check if it's an image URL
        const isImage = value.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)/i);
        
        if (!showUrls && value.length > 60) {
          return (
            <span className="jdv-json-string jdv-json-url">
              <Tooltip title={value}>
                <a href={value} target="_blank" rel="noopener noreferrer">
                  {value.substring(0, 50)}...
                </a>
              </Tooltip>
              {isImage && (
                <img 
                  src={value} 
                  alt="" 
                  className="jdv-json-image-preview"
                  style={{ maxWidth: 60, maxHeight: 40, marginLeft: 8, borderRadius: 4 }}
                />
              )}
            </span>
          );
        }
        
        return (
          <span className="jdv-json-string jdv-json-url">
            <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>
          </span>
        );
      }
      
      // Long strings
      if (!expanded && value.length > 100) {
        return (
          <span className="jdv-json-string">
            "{value.substring(0, 100)}..."
          </span>
        );
      }
      
      return <span className="jdv-json-string">"{value}"</span>;
    }
    
    if (Array.isArray(value)) {
      if (!expanded && depth > 1 && value.length > 3) {
        return <span className="jdv-json-collapsed">[{value.length} items]</span>;
      }
      
      return (
        <span className="jdv-json-array">
          [
          {value.length > 0 && (
            <div className="jdv-json-indent">
              {value.map((item, index) => (
                <div key={index} className="jdv-json-item">
                  {renderValue(item, index, depth + 1)}
                  {index < value.length - 1 && ','}
                </div>
              ))}
            </div>
          )}
          ]
        </span>
      );
    }
    
    if (typeof value === 'object') {
      const entries = Object.entries(value);
      
      if (!expanded && depth > 1 && entries.length > 5) {
        return <span className="jdv-json-collapsed">{`{${entries.length} properties}`}</span>;
      }
      
      return (
        <span className="jdv-json-object">
          {'{'}
          {entries.length > 0 && (
            <div className="jdv-json-indent">
              {entries.map(([k, v], index) => (
                <div key={k} className="jdv-json-item">
                  <span className="jdv-json-key">"{k}"</span>: {renderValue(v, k, depth + 1)}
                  {index < entries.length - 1 && ','}
                </div>
              ))}
            </div>
          )}
          {'}'}
        </span>
      );
    }
    
    return <span>{JSON.stringify(value)}</span>;
  };

  const paramCount = Object.keys(filteredParams).length;

  return (
    <div className="jdv-params-viewer">
      <div className="jdv-params-header">
        <div className="jdv-params-title">
          <Typography.Title level={5}>{title}</Typography.Title>
          <span className="jdv-count-badge">{paramCount} params</span>
        </div>
        
        <Space size={8} wrap>
          <Input
            placeholder="Search params..."
            prefix={<SearchOutlined />}
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 150 }}
            allowClear
          />
          
          <Tooltip title="Show full URLs">
            <Switch
              size="small"
              checked={showUrls}
              onChange={setShowUrls}
              checkedChildren="URLs"
              unCheckedChildren="URLs"
            />
          </Tooltip>
          
          <Button
            size="small"
            icon={expanded ? <CompressOutlined /> : <ExpandOutlined />}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Collapse' : 'Expand'}
          </Button>
          
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={handleCopyAll}
          >
            Copy JSON
          </Button>
          
          {showReuseButton && onReuseParams && (
            <Button
              type="primary"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => onReuseParams?.(data)}
            >
              Re-use Params
            </Button>
          )}
        </Space>
      </div>
      
      <div className="jdv-params-content">
        {paramCount === 0 ? (
          <div className="jdv-params-empty">
            {searchTerm ? 'No parameters match your search' : 'No parameters available'}
          </div>
        ) : (
          <pre className="jdv-json-pre">
            {'{'}
            <div className="jdv-json-indent">
              {Object.entries(filteredParams).map(([key, value], index) => (
                <div key={key} className="jdv-json-item jdv-json-root-item">
                  <span className="jdv-json-key">"{key}"</span>: {renderValue(value, key, 0)}
                  {index < paramCount - 1 && ','}
                </div>
              ))}
            </div>
            {'}'}
          </pre>
        )}
      </div>
    </div>
  );
};

export default ParamsViewer;
